import React, { useEffect, useState } from "react";
import ConsentClasses from "./index.module.css";
import Layout from "Components/Layout";
import { Helmet } from "react-helmet";
import { Services } from "Services";
import SkeletonLoad from "Components/TableLoad/load";
import { Error, Error_Dark, warningSwal } from "Util/Toast";
import Moment from "react-moment";
import ReactPaginate from "react-paginate";
import { useForm } from "react-hook-form";

const CONSENT_TYPES = [
    { value: "child_data", label: "Child Data" },
    { value: "educational_updates", label: "Educational Updates" },
    { value: "marketing_promotional", label: "Marketing / Promotional" },
];

const TABLE_HEAD = [
    "Date",
    "Parent",
    "Mobile Number",
    "Email",
    "Student",
    "Consent Type",
    "Status",
    "Channel",
    "IP Address",
    "User Agent",
];

const LIMIT = 10;

// only super admin (Type 1) sees full mobile numbers; others see last 4 digits
const maskMobile = (number) => {
    const value = String(number || "");
    if (value.length <= 4) return value;
    return "x".repeat(value.length - 4) + value.slice(-4);
};

const consentTypeLabel = (type) =>
    CONSENT_TYPES.find((item) => item.value === type)?.label || type;

function ParentConsents() {
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({});
    const [page, setPage] = useState(1);
    const [pagination, setPagination] = useState({ Total: 0, TotalPages: 0 });
    const token = { Authorization: `token ${localStorage.getItem("token")}` };
    const isSuperAdmin =
        JSON.parse(localStorage.getItem("admindata") || "{}")?.Type == 1;
    // other admins only see records after searching with at least one filter
    const hasFilters = Object.keys(filters).length > 0;
    const canList = isSuperAdmin || hasFilters;
    const [loading, setLoading] = useState(canList);

    const { register, handleSubmit, reset } = useForm();

    const getConsentHistory = (params, pageNumber) => {
        setLoading(true);
        Services.getConsentHistory("GET", null, token, {
            ...params,
            Page: pageNumber,
            Limit: LIMIT,
        })
            .then((response) => {
                setTimeout(() => {
                    setLoading(false);
                }, 200);
                if (response.Status === 1) {
                    setData(response.Results || []);
                    setPagination(
                        response.Pagination || { Total: 0, TotalPages: 0 }
                    );
                } else if (response.Status === 0) {
                    Error(response.Message);
                }
            })
            .catch((err) => {
                setLoading(false);
                Error_Dark("something went wrong please try again");
                console.log(err);
            });
    };

    useEffect(() => {
        if (!canList) {
            setData([]);
            setPagination({ Total: 0, TotalPages: 0 });
            setLoading(false);
            return;
        }
        getConsentHistory(filters, page);
        // eslint-disable-next-line
    }, [filters, page]);

    const onSubmit = (formData) => {
        if (
            formData.FromDate &&
            formData.ToDate &&
            formData.FromDate > formData.ToDate
        ) {
            warningSwal("Warning", "From Date cannot be after To Date");
            return;
        }
        // send only the filters that were filled in
        const params = {};
        Object.entries(formData).forEach(([key, value]) => {
            if (value && String(value).trim()) {
                params[key] = String(value).trim();
            }
        });
        if (!isSuperAdmin && Object.keys(params).length === 0) {
            warningSwal("Warning", "Enter at least one filter to search");
            return;
        }
        setPage(1);
        setFilters(params);
    };

    const clearHandler = () => {
        reset({
            FromDate: "",
            ToDate: "",
            EmailID: "",
            MobileNumber: "",
            ConsentType: "",
            Status: "",
        });
        setPage(1);
        setFilters({});
    };

    const handlePageChange = (selected) => {
        window.scrollTo({ top: 10, behavior: "smooth" });
        setPage(selected.selected + 1);
    };

    return (
        <Layout Active={"Parent Consents"}>
            <Helmet>
                <meta charSet="utf-8" />
                <title>Parent Consents</title>
            </Helmet>
            <div className={ConsentClasses["Container"]}>
                <form
                    className={ConsentClasses["filter"]}
                    onSubmit={handleSubmit(onSubmit)}>
                    <input
                        type="text"
                        placeholder="From Date"
                        title="From Date"
                        {...register("FromDate")}
                        onFocus={(e) => (e.target.type = "date")}
                    />
                    <input
                        type="text"
                        placeholder="To Date"
                        title="To Date"
                        {...register("ToDate")}
                        onFocus={(e) => (e.target.type = "date")}
                    />
                    <input
                        type="email"
                        placeholder="Email ID"
                        {...register("EmailID")}
                    />
                    <input
                        type="text"
                        placeholder="Mobile Number"
                        {...register("MobileNumber")}
                    />
                    <select {...register("ConsentType")}>
                        <option value="">All Consent Types</option>
                        {CONSENT_TYPES.map((item) => (
                            <option key={item.value} value={item.value}>
                                {item.label}
                            </option>
                        ))}
                    </select>
                    <select {...register("Status")}>
                        <option value="">All Status</option>
                        <option value="1">Granted</option>
                        <option value="0">Withdrawn</option>
                    </select>
                    <button type="submit" className={ConsentClasses["button"]}>
                        Search
                    </button>
                    <button
                        type="button"
                        className={ConsentClasses["button"]}
                        onClick={clearHandler}>
                        Clear
                    </button>
                </form>
                <div className={ConsentClasses["Tutor"]}>
                    <h3>
                        Parent Consents
                        {!loading && canList && (
                            <span className={ConsentClasses["total"]}>
                                ({pagination.Total || 0})
                            </span>
                        )}
                    </h3>
                    {loading ? (
                        <SkeletonLoad TableHead={TABLE_HEAD} />
                    ) : data.length > 0 ? (
                        <div className={ConsentClasses["Table"]}>
                            <table>
                                <thead>
                                    <tr>
                                        {TABLE_HEAD.map((head) => (
                                            <th key={head}>{head}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.map((item) => (
                                        <tr key={item.ParentConsentID}>
                                            <td className={ConsentClasses["date"]}>
                                                <Moment format="DD-MM-YYYY hh:mm A">
                                                    {new Date(item.OccurredAt)}
                                                </Moment>
                                            </td>
                                            <td className={ConsentClasses["parent-name"]}>
                                                {item.ParentName}
                                            </td>
                                            <td>
                                                {item.MobileNumber
                                                    ? isSuperAdmin
                                                        ? item.MobileNumber
                                                        : maskMobile(
                                                              item.MobileNumber
                                                          )
                                                    : "-"}
                                            </td>
                                            <td>{item.ParentEmail || "-"}</td>
                                            <td>{item.StudentName || "-"}</td>
                                            <td>
                                                {consentTypeLabel(
                                                    item.ConsentType
                                                )}
                                                {item.IsRequired === 1 && (
                                                    <small
                                                        className={
                                                            ConsentClasses[
                                                                "required"
                                                            ]
                                                        }>
                                                        Required
                                                    </small>
                                                )}
                                            </td>
                                            <td
                                                className={
                                                    item.Granted === 1
                                                        ? ConsentClasses[
                                                              "Status-active"
                                                          ]
                                                        : ConsentClasses[
                                                              "Status-inactive"
                                                          ]
                                                }>
                                                <span>
                                                    {item.Granted === 1
                                                        ? "Granted"
                                                        : "Withdrawn"}
                                                </span>
                                            </td>
                                            <td>{item.Channel}</td>
                                            <td>{item.IPAddress || "-"}</td>
                                            <td
                                                className={
                                                    ConsentClasses["consent-text"]
                                                }
                                                title={item.UserAgent}>
                                                {item.UserAgent || "-"}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div>
                            <p>
                                {canList
                                    ? "No Consent Records Found"
                                    : "Use the filters above and click Search to view consent records"}
                            </p>
                        </div>
                    )}
                    {pagination.TotalPages > 1 && (
                        <div style={{ justifySelf: "right" }}>
                            <ReactPaginate
                                pageCount={pagination.TotalPages}
                                forcePage={page - 1}
                                marginPagesDisplayed={2}
                                onPageChange={handlePageChange}
                                breakClassName={"page-item"}
                                breakLinkClassName={"page-link"}
                                containerClassName={"pagination"}
                                pageClassName={"page-item"}
                                pageLinkClassName={"page-link"}
                                previousClassName={"page-item"}
                                previousLinkClassName={"page-link"}
                                nextClassName={"page-item"}
                                nextLinkClassName={"page-link"}
                                activeClassName={"active"}
                            />
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
}

export default ParentConsents;
